import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";

export default function WebsiteCourseFeatures() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [courses, setCourses] = useState([]);
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/course-features",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Course Features");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
        requestAPI({
            requestPath: "website/courses",
            requestMethod: "GET",
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setCourses(data);
            },
        });
    }, [loadRows, requestAPI]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Course points" />
            <TabHeader
                className="px-3 pt-3"
                title="Course points"
                highlights={["Each point is a short line on the course card. The card shows the first 3 points"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} courses={courses} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} courses={courses} rows={rows} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function courseName(courses, publicId) {
    return courses.find((course) => course.public_id === publicId)?.title || "Course not found";
}

function Row({ row, courses, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/course-features/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Course point removed", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    return (
        <div className="flex gap-3 align-items-center border-round py-2 px-3 text-white bg-green-500">
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.title || "Untitled"}</span>
                <span className={TEXT_SMALL}>{courseName(courses, row.course_public_id)}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function saveCall(requestAPI, options) {
    return new Promise((resolve) => {
        requestAPI({
            ...options,
            onResponseReceieved: (data, responseCode) => resolve({ data, responseCode }),
            onRequestFailure: () => resolve({ data: null, responseCode: 0 }),
        });
    });
}

function Editor({ dialog, courses, rows, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ ...(dialog.record || {}) });
    const [courseIds, setCourseIds] = useState(dialog.record?.course_public_id ? [dialog.record.course_public_id] : []);
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const courseOptions = courses.filter((course) => course.public_id && course.title);

    const save = async () => {
        const title = (record.title || "").trim();
        if (!title) {
            showToast({ severity: "error", summary: "Missing", detail: "Write the point that should appear on the card", life: 2000 });
            return;
        }
        if (!courseIds.length) {
            showToast({ severity: "error", summary: "Missing", detail: "Select at least one course", life: 2000 });
            return;
        }

        const body = { icon: record.icon || "", title };
        setSaving(true);
        const saved = [];
        const originalId = dialog.record?.course_public_id;
        const keepId = !isNew && courseIds.includes(originalId) ? originalId : courseIds[0];
        const extraIds = courseIds.filter((id) => id !== keepId && !(rows || []).some((row) => row.course_public_id === id && row.title === title));

        if (!isNew) {
            const updated = await saveCall(requestAPI, {
                requestPath: "website/course-features",
                requestMethod: "PATCH",
                requestPostBody: { ...body, id: record.id, course_public_id: keepId },
            });
            if (updated.data && updated.responseCode === 200) saved.push(updated.data);
        } else {
            const created = await saveCall(requestAPI, {
                requestPath: "website/course-features",
                requestMethod: "POST",
                requestPostBody: { ...body, course_public_id: keepId },
            });
            if (created.data && created.responseCode === 201) saved.push(created.data);
        }

        for (const courseId of extraIds) {
            const created = await saveCall(requestAPI, {
                requestPath: "website/course-features",
                requestMethod: "POST",
                requestPostBody: { ...body, course_public_id: courseId },
            });
            if (created.data && created.responseCode === 201) saved.push(created.data);
        }

        setSaving(false);
        if (!saved.length) {
            showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 });
            return;
        }
        setRows((prev) => {
            const next = [...(prev || [])];
            saved.forEach((item) => {
                const index = next.findIndex((row) => row.id === item.id);
                if (index >= 0) next[index] = item;
                else next.push(item);
            });
            return next;
        });
        showToast({ severity: "success", summary: "Saved", detail: saved.length > 1 ? `Point added to ${saved.length} courses` : "Course point saved", life: 1000 });
        setDialog({ visible: false });
    };

    return (
        <Dialog header={isNew ? "Add a course point" : "Edit course point"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <MultiSelect
                            inputId="courses"
                            className="w-full"
                            options={courseOptions}
                            optionLabel="title"
                            optionValue="public_id"
                            value={courseIds}
                            onChange={(event) => setCourseIds(event.value || [])}
                            disabled={saving || !courseOptions.length}
                            display="chip"
                            placeholder="Select courses"
                            filter
                        />
                        <label htmlFor="courses">Courses</label>
                    </FloatLabel>
                    <FieldNote text="Select every course that should show this point. The same point can be on more than one course." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Point text</label>
                    </FloatLabel>
                    <FieldNote text="The short line on the course card, such as Weekly Tests. A card shows the first 3 points." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="icon" className="w-full" value={record?.icon ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, icon: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="icon">Icon name</label>
                    </FloatLabel>
                    <FieldNote text="Optional Material icon name, such as quiz or schedule. Leave blank to use a tick." />
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
